import { Link, useParams } from 'react-router-dom';
import {
  StatePanel,
  V2Badge,
  V2Card,
  V2CardHeader,
  V2EmptyState,
  V2Skeleton,
} from '@sakhtar/design-system/react';
import { useApiGet, type ApiGetState } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import {
  NO_PROJECT_LABEL,
  type OverdueProjectListResponse,
  type OverdueTaskListResponse,
} from '../../lib/types/projects.js';
import './project-lists.css';

const fa = (n: number) => n.toLocaleString('fa-IR');

function OverdueSkeleton() {
  return (
    <div aria-busy="true" aria-live="polite">
      {[0, 1, 2].map((i) => (
        <div key={i} className="prj-overdue__row">
          <V2Skeleton height={12} />
          <V2Skeleton width={64} height={22} />
        </div>
      ))}
    </div>
  );
}

function SectionError({ res }: { readonly res: ApiGetState<unknown> }) {
  if (res.status !== 'error') return null;
  return (
    <StatePanel
      variant={res.error.status === 403 ? 'permission_denied' : 'network_error'}
      title={res.error.status === 403 ? 'دسترسی ندارید' : 'قابل نمایش نیست'}
    />
  );
}

/**
 * UI_REFERENCE_SOURCE: no dedicated Design Board — follows the design
 * system's own primitives directly, per the route inventory's PO decision.
 * canonical pattern: two read-only overdue lists (projects, tasks), worst first
 */
export function OverduePage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const projects = useApiGet<OverdueProjectListResponse>(
    slug ? `${base}/api/v1/projects/overdue/projects` : null,
    [slug],
  );
  const tasks = useApiGet<OverdueTaskListResponse>(
    slug ? `${base}/api/v1/projects/overdue/tasks` : null,
    [slug],
  );

  if (!slug) return null;

  const projectRows =
    projects.status === 'ready'
      ? [...projects.data.projects].sort((a, b) => b.daysOverdue - a.daysOverdue)
      : [];
  const taskRows =
    tasks.status === 'ready'
      ? [...tasks.data.tasks].sort((a, b) => b.daysOverdue - a.daysOverdue)
      : [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <V2Card clipped>
        <V2CardHeader
          icon="projects"
          title="پروژه‌های عقب‌افتاده"
          subtitle={projects.status === 'ready' ? `${fa(projectRows.length)} مورد` : undefined}
        />
        {projects.status === 'loading' ? <OverdueSkeleton /> : null}
        <SectionError res={projects} />
        {projects.status === 'ready' ? (
          projectRows.length === 0 ? (
            <V2EmptyState
              compact
              icon="check"
              title="پروژه عقب‌افتاده‌ای نیست."
              description="همهٔ پروژه‌ها در موعد مقرر هستند."
            />
          ) : (
            projectRows.map((p) => (
              <div key={p.ref} className="prj-overdue__row">
                <div className="prj-overdue__main">
                  <Link
                    to={`${base}/projects/list/${encodeURIComponent(p.ref)}`}
                    className="prj-overdue__link"
                  >
                    {p.name}
                  </Link>
                  <span className="prj-overdue__sub">{p.ref}</span>
                </div>
                <span className="prj-overdue__days">
                  <V2Badge tone="danger">{fa(p.daysOverdue)} روز تأخیر</V2Badge>
                </span>
              </div>
            ))
          )
        ) : null}
      </V2Card>

      <V2Card clipped>
        <V2CardHeader
          icon="tasks"
          title="کارهای عقب‌افتاده"
          subtitle={tasks.status === 'ready' ? `${fa(taskRows.length)} مورد` : undefined}
        />
        {tasks.status === 'loading' ? <OverdueSkeleton /> : null}
        <SectionError res={tasks} />
        {tasks.status === 'ready' ? (
          taskRows.length === 0 ? (
            <V2EmptyState
              compact
              icon="check"
              title="کار عقب‌افتاده‌ای نیست."
              description="هیچ کاری از سررسید خود نگذشته است."
            />
          ) : (
            taskRows.map((t) => (
              <div key={t.ref} className="prj-overdue__row">
                <div className="prj-overdue__main">
                  <Link
                    to={`${base}/projects/tasks/${encodeURIComponent(t.ref)}`}
                    className="prj-overdue__link"
                  >
                    {t.title}
                  </Link>
                  <span className="prj-overdue__sub">
                    {t.projectRef ? (
                      <Link to={`${base}/projects/list/${encodeURIComponent(t.projectRef)}`}>
                        {t.projectRef}
                      </Link>
                    ) : (
                      NO_PROJECT_LABEL
                    )}
                  </span>
                </div>
                <span className="prj-overdue__days">
                  <V2Badge tone="danger">{fa(t.daysOverdue)} روز تأخیر</V2Badge>
                </span>
              </div>
            ))
          )
        ) : null}
      </V2Card>
    </div>
  );
}
